import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import Header from "./Header";
import Footer from "./Footer";

const ServiceCards = () => {
  const navigate = useNavigate();
  const { serviceGroupId } = useParams();
  const { state } = useLocation();

  const [serviceGroup, setServiceGroup] = useState(state?.group || null);
  const [loading, setLoading] = useState(!state?.group);

  useEffect(() => {
    if (state?.group) return;

    const BASE_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${BASE_URL}/api/public/services-menu`)
      .then((res) => res.json())
      .then((data) => {
        const found = Array.isArray(data)
          ? data.find((g) => g.id === serviceGroupId)
          : null;
        setServiceGroup(found || null);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load services menu", err);
        setLoading(false);
      });
  }, [serviceGroupId]);

  const handleSubServiceClick = (subService) => {
    navigate(`/services/${subService.id}`);
  };

  if (loading) {
    return (
      <>
        <Header />
        <div
          style={{
            minHeight: "60vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "3px solid rgba(201,168,76,0.2)",
              borderTop: "3px solid #c9a84c",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }}
          />
          <p style={{ color: "#7a98a8", fontSize: "14px" }}>
            Loading services...
          </p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
        <Footer />
      </>
    );
  }

  if (!serviceGroup) {
    return (
      <>
        <Header />
        <div
          style={{
            padding: "100px 20px",
            textAlign: "center",
            minHeight: "60vh",
          }}
        >
          <p style={{ color: "#546e7a", marginBottom: "16px" }}>
            Service not found
          </p>
          <button
            onClick={() => navigate("/")}
            style={{
              padding: "10px 20px",
              background: "#c9a84c",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            Go back home
          </button>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,500;0,600;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');

        .ssp-page {
          font-family: 'DM Sans', sans-serif;
          background: #f4f7f9;
          min-height: 100vh;
          color: #1a2e3d;
        }

        .ssp-hero {
          background: linear-gradient(135deg, #0f1f2e 0%, #1a3a52 100%);
          padding: 56px 32px 48px;
          border-bottom: 2px solid #c9a84c;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .ssp-hero::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -10%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(201,168,76,0.08) 0%, transparent 70%);
          border-radius: 50%;
        }

        .ssp-hero::after {
          content: '';
          position: absolute;
          bottom: -30%;
          left: -5%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(201,168,76,0.05) 0%, transparent 70%);
          border-radius: 50%;
        }

        .ssp-hero-inner {
          max-width: 860px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .ssp-eyebrow {
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #c9a84c;
          margin-bottom: 12px;
        }

        .ssp-hero h1 {
          font-family: 'Lora', serif;
          font-size: 36px;
          font-weight: 500;
          color: #d9e8f0;
          margin-bottom: 12px;
          line-height: 1.25;
        }

        .ssp-hero p {
          font-size: 14px;
          color: #7a98a8;
          line-height: 1.75;
          max-width: 560px;
          margin: 0 auto;
        }

        .ssp-divider {
          width: 64px;
          height: 3px;
          background: #c9a84c;
          margin: 20px auto 0;
          border-radius: 2px;
        }

        .ssp-body {
          max-width: 1100px;
          margin: 0 auto;
          padding: 56px 32px 72px;
        }

        .ssp-back-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 500;
          color: #4a6475;
          background: #fff;
          border: 1px solid rgba(15,31,46,0.1);
          border-radius: 8px;
          padding: 10px 18px;
          cursor: pointer;
          margin-bottom: 36px;
          transition: all 0.2s;
        }

        .ssp-back-btn:hover {
          color: #c9a84c;
          border-color: rgba(201,168,76,0.3);
          transform: translateX(-2px);
        }

        .ssp-section-title {
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #c9a84c;
          margin-bottom: 24px;
          padding-bottom: 10px;
          border-bottom: 1px solid rgba(201,168,76,0.2);
        }

        .ssp-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 18px;
        }

        .ssp-card {
          background: #fff;
          border: 1px solid rgba(15,31,46,0.08);
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.25s ease;
          position: relative;
        }

        .ssp-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #c9a84c, #e8c96d);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.3s ease;
        }

        .ssp-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(15,31,46,0.12);
          border-color: rgba(201,168,76,0.3);
        }

        .ssp-card:hover::before {
          transform: scaleX(1);
        }

        .ssp-card-inner {
          padding: 24px;
          display: flex;
          align-items: flex-start;
          gap: 16px;
        }

        .ssp-card-icon {
          width: 52px;
          height: 52px;
          flex-shrink: 0;
          border-radius: 10px;
          background: rgba(201,168,76,0.08);
          border: 1px solid rgba(201,168,76,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          transition: all 0.25s;
        }

        .ssp-card:hover .ssp-card-icon {
          background: rgba(201,168,76,0.15);
          border-color: rgba(201,168,76,0.35);
          transform: scale(1.05);
        }

        .ssp-card-content {
          flex: 1;
          min-width: 0;
        }

        .ssp-card-title {
          font-size: 16px;
          font-weight: 600;
          color: #1a2e3d;
          margin-bottom: 6px;
          display: flex;
          align-items: center;
          gap: 8px;
          line-height: 1.4;
        }

        .ssp-card-arrow {
          color: #a0b8c4;
          flex-shrink: 0;
          transition: all 0.2s;
          margin-left: auto;
        }

        .ssp-card:hover .ssp-card-arrow {
          color: #c9a84c;
          transform: translateX(4px);
        }

        .ssp-card-desc {
          font-size: 13px;
          color: #546e7a;
          line-height: 1.6;
        }

        .ssp-empty {
          text-align: center;
          padding: 60px 20px;
          background: #fff;
          border: 1px solid rgba(15,31,46,0.08);
          border-radius: 12px;
        }

        .ssp-empty-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: rgba(201,168,76,0.08);
          border: 1px solid rgba(201,168,76,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
          color: #c9a84c;
          font-size: 28px;
        }

        .ssp-empty-text {
          font-size: 14px;
          color: #8aadbe;
        }

        @media (max-width: 640px) {
          .ssp-hero { padding: 40px 20px 36px; }
          .ssp-hero h1 { font-size: 28px; }
          .ssp-body { padding: 36px 16px 52px; }
          .ssp-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <Header />

      <div className="ssp-page">
        {/* Hero */}
        <div className="ssp-hero">
          <div className="ssp-hero-inner">
            <h1>{serviceGroup.name}</h1>
            <p>
              Choose from our range of {serviceGroup.name.toLowerCase()} services
              to get started with your application
            </p>
            <div className="ssp-divider" />
          </div>
        </div>

        {/* Body */}
        <div className="ssp-body">
          <button className="ssp-back-btn" onClick={() => navigate("/")}>
            <ArrowLeft size={16} strokeWidth={2} />
          </button>

          <p className="ssp-section-title">Available Services</p>

          {serviceGroup.services && serviceGroup.services.length > 0 ? (
            <div className="ssp-grid">
              {serviceGroup.services.map((subService) => (
                <div
                  key={subService.id}
                  className="ssp-card"
                  onClick={() => handleSubServiceClick(subService)}
                >
                  <div className="ssp-card-inner">
                    <div className="ssp-card-icon">📄</div>
                    <div className="ssp-card-content">
                      <p className="ssp-card-title">
                        {subService.name}
                        <ChevronRight
                          size={16}
                          className="ssp-card-arrow"
                          strokeWidth={2}
                        />
                      </p>
                      {subService.description && (
                        <p className="ssp-card-desc">{subService.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="ssp-empty">
              <div className="ssp-empty-icon">📋</div>
              <p className="ssp-empty-text">
                No services available in this category at the moment
              </p>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
};

export default ServiceCards;